import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, of, tap, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { ShellNavItem } from '../models/shell-nav-item';

@Injectable({
  providedIn: 'root'
})
export class ShellNavService {
  private readonly fallbackItems: ShellNavItem[] = [
    {
      description: 'AgVantis monitored resources and Azure Service Health',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'monitored-resources',
      screenKey: 'MonitoredResources',
      screenKind: 'Native',
      sequence: 10,
      targetUrl: '',
      team: 'Integrations',
      title: 'Monitored Resources'
    },
    {
      description: 'Search contacts by name or external id',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'contact-search',
      screenKey: 'ContactSearch',
      screenKind: 'Native',
      sequence: 20,
      targetUrl: '',
      team: 'Integrations',
      title: 'Contact Search'
    },
    {
      description: 'Flow process logs by correlation id',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'correlation-search',
      screenKey: 'CorrelationSearch',
      screenKind: 'Native',
      sequence: 30,
      targetUrl: '',
      team: 'Integrations',
      title: 'Correlation Search'
    },
    {
      description: 'Potential bookings by association',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'month-end',
      screenKey: 'MonthEnd',
      screenKind: 'Native',
      sequence: 40,
      targetUrl: '',
      team: 'Integrations',
      title: 'Month End'
    },
    {
      description: 'Automic system health, running work, and alerts',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'automic',
      screenKey: 'Automic',
      screenKind: 'Native',
      sequence: 10,
      targetUrl: '',
      team: 'Automic',
      title: 'Overview'
    },
    {
      description: 'Current and historical Automic workflows',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'automic-executions',
      screenKey: 'AutomicExecutions',
      screenKind: 'Native',
      sequence: 20,
      targetUrl: '',
      team: 'Automic',
      title: 'Workflow History'
    },
    {
      description: 'Test coverage and SonarCloud code quality by repository',
      enabled: true,
      iconUrl: '',
      openMode: 'Embed',
      rowKey: 'repository-quality',
      screenKey: 'RepositoryQuality',
      screenKind: 'Native',
      sequence: 10,
      targetUrl: '',
      team: 'Quality Assurance',
      title: 'Repository Quality'
    }
  ];
  private readonly http = inject(HttpClient);
  private readonly itemsSignal = signal<ShellNavItem[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly loadedSignal = signal(false);

  readonly error = signal<string | null>(null);
  readonly items = this.itemsSignal.asReadonly();
  readonly loaded = this.loadedSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly teams = computed(() => {
    const teams = [...new Set(this.itemsSignal().map((item) => item.team || 'Other'))];
    return teams.sort((left, right) => left.localeCompare(right));
  });

  firstRoutableItem(): ShellNavItem | undefined {
    return this.teams()
      .flatMap((team) => this.itemsForTeam(team))
      .find((item) => {
        return !(item.screenKind === 'Url' && (item.openMode === 'NewTab' || item.openMode === 'SameWindow'));
      });
  }

  itemsForTeam(team: string): ShellNavItem[] {
    return this.itemsSignal().filter((item) => (item.team || 'Other') === team);
  }

  load(): void {
    if (this.loadingSignal()) {
      return;
    }

    if (this.loadedSignal() && this.itemsSignal().length > 0) {
      return;
    }

    this.loadingSignal.set(true);
    this.http
      .get<unknown>(`${environment.observabilityService.url}ShellNav`)
      .pipe(
        timeout({ first: 15000 }),
        tap((payload) => {
          this.setItems(this.recordsFrom(payload).map((item) => this.mapItem(item)));
          this.error.set(null);
        }),
        catchError(() => {
          this.setItems([]);
          return of([]);
        })
      )
      .subscribe();
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private mapItem(item: Record<string, unknown>): ShellNavItem {
    const readNumber = (camel: string, pascal: string): number => Number(item[camel] ?? item[pascal] ?? 0);
    const readString = (camel: string, pascal: string): string => String(item[camel] ?? item[pascal] ?? '');

    return {
      description: readString('description', 'Description'),
      enabled: this.readEnabled(item),
      iconUrl: readString('iconUrl', 'IconUrl'),
      openMode: readString('openMode', 'OpenMode') as ShellNavItem['openMode'],
      rowKey: readString('rowKey', 'RowKey'),
      screenKey: readString('screenKey', 'ScreenKey'),
      screenKind: readString('screenKind', 'ScreenKind') as ShellNavItem['screenKind'],
      sequence: readNumber('sequence', 'Sequence'),
      targetUrl: readString('targetUrl', 'TargetUrl'),
      team: readString('team', 'Team'),
      title: readString('title', 'Title')
    };
  }

  private readEnabled(item: Record<string, unknown>): boolean {
    const raw = item['enabled'] ?? item['Enabled'] ?? item['isEnabled'] ?? item['IsEnabled'];
    if (raw === undefined || raw === null || raw === '') {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false' && raw !== '0';
    }

    return Boolean(raw);
  }

  private recordsFrom(payload: unknown): Record<string, unknown>[] {
    if (Array.isArray(payload)) {
      return payload.filter((item): item is Record<string, unknown> => this.isRecord(item));
    }

    if (!this.isRecord(payload)) {
      return [];
    }

    const nested = payload['value'] ?? payload['items'] ?? payload['data'] ?? payload['result'];
    if (nested !== undefined) {
      return this.recordsFrom(nested);
    }

    return [payload];
  }

  private setItems(items: ShellNavItem[]): void {
    const enabled = items.filter((item) => item.enabled);
    const present = new Set(enabled.map((item) => item.rowKey));
    const merged = enabled.length === 0 ? this.fallbackItems : [
      ...enabled,
      ...this.fallbackItems.filter((item) => item.team === 'Automic' && !present.has(item.rowKey))
    ];
    this.itemsSignal.set([...merged].sort(
      (left, right) => left.sequence - right.sequence || left.title.localeCompare(right.title)
    ));
    this.loadingSignal.set(false);
    this.loadedSignal.set(true);
  }
}
